import { defineStore } from 'pinia'
import { useUserSession } from '/@src/stores/userSession'
import { useApiAuth } from '../composable/useApiAuth'
import { useApi } from '../composable/useApi'
import { useNotyf } from '../composable/useNotyf'
import { ref } from 'vue'

export const useAuth = defineStore('auth', () => {
  const api = useApiAuth()
  const apiMe = useApi()
  const userSession = useUserSession()
  const notif = useNotyf()
  const loading = ref(false)
  const isLoggingOut = ref(false)

  // Initiate SSO login
  const initiateSSO = async (redirectAfterLogin?: string) => {
    try {
      const ssoLoginUrl = import.meta.env.VITE_BASE_SSO
      if (!ssoLoginUrl) {
        throw new Error('SSO login URL not configured')
      }

      // Build the return URL (where to redirect after SSO login)
      const returnUrl = `${window.location.origin}/auth/callback`

      // Add redirect path if specified
      const redirectParam = redirectAfterLogin ?
        `?redirect=${encodeURIComponent(redirectAfterLogin)}` :
        ''

      // Redirect to SSO login page
      window.location.href = `${ssoLoginUrl}?redirect_uri=${encodeURIComponent(returnUrl + redirectParam)}`
    } catch (error) {
      console.error('Failed to initiate SSO login:', error)
      throw error
    }
  }

  const logout = async () => {
    // Prevent multiple logout attempts
    if (isLoggingOut.value) return false

    try {
      isLoggingOut.value = true
      notif.info('Logging out...')

      const refreshToken = userSession.refreshToken

      // Clear all cookies
      document.cookie.split(";").forEach(function(c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/")
      })

      // Call the logout API
      try {
        await apiMe.post('/v1/auth/logout', {
          timeout: 10000,
          validateStatus: () => true
        })
      } catch (apiError) {
        console.error('Logout API call failed:', apiError)
      }

      // Clear user session data
      await userSession.logoutUser()

      // Clear localStorage (except essential settings)
      const preserveKeys = ['theme', 'language']
      Object.keys(localStorage).forEach(key => {
        if (!preserveKeys.includes(key)) {
          localStorage.removeItem(key)
        }
      })

      // Clear sessionStorage
      sessionStorage.clear()

      // Redirect to SSO login
      const ssoUrl = import.meta.env.VITE_BASE_SSO
      console.log('Redirecting to SSO:', ssoUrl)

      // Use replace for a clean redirect
      window.location.replace(ssoUrl)
      return true
    } catch (error) {
      console.error('Logout error:', error)
      notif.error('Logout failed, please try again')
      isLoggingOut.value = false

      // Fallback redirect attempt
      try {
        window.location.href = import.meta.env.VITE_BASE_SSO
      } catch (e) {
        console.error('Fallback redirect failed:', e)
      }

      return false
    }
  }

  const fetchMe = async () => {
    try {
      // Do api request call to retrieve user profile
      const { data } = await apiMe.get('/user/me')
      userSession.setUser(data)
      return data
    } catch (err) {
      // delete stored token if it fails
      userSession.logoutUser()
      throw err
    }
  }

  // Token refresh function
  const refreshAuthToken = async () => {
    if (!userSession.refreshToken) {
      throw new Error('No refresh token available')
    }

    try {
      const { data } = await apiMe.post('/v1/auth/refresh', {
        refresh_token: userSession.refreshToken
      })

      if (data.token) {
        userSession.setToken(data.token)
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to refresh token:', error)
      // If refresh fails, log the user out
      await logout()
      return false
    }
  }

  return {
    initiateSSO,
    logout,
    fetchMe,
    refreshAuthToken,
    loading,
    isLoggingOut
  }
})
